<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AppPromptInput from '../AppPromptInput/AppPromptInput.vue'
import AppPromptAttachments from '../AppPromptAttachments/AppPromptAttachments.vue'
import AppContextChips from '../AppChatPanel/AppContextChips.vue'
import type { ContextItem } from '../AppChatPanel/context-types'
import AgentAsk from '../AgentAsk/AgentAsk.vue'
import { useChatThreadSwitcher } from '@/composables/useChatThreadSwitcher'
import {
  type PendingImageHandoff,
  usePromptImageAttachments,
} from '@/composables/usePromptImageAttachments'
import { useConnectionsStore } from '@/stores/connections'
import {
  askFromAction,
  askFromConnect,
  askFromQuestion,
  questionOptionIndex,
  ACTION_CHOICE,
  CONNECT_CHOICE,
  QUESTION_FREE_TEXT_CHOICE,
  type AgentAskModel,
  type AgentAskResolveBody,
} from '@/lib/chat/agent-ask'
import {
  MAX_MESSAGE_ATTACHMENTS,
  type PendingAction,
  type PendingConnect,
  type PendingQuestion,
} from '@/types/chat'

const props = withDefaults(
  defineProps<{
    modelValue: string
    openAction?: PendingAction
    openQuestion?: PendingQuestion
    /** True once the user chose to type a free-text answer instead of picking a question's chips. */
    questionTyping?: boolean
    connect?: PendingConnect
    pending?: boolean
    status?: 'ready' | 'submitting' | 'streaming' | 'error'
    retryable?: boolean
    disabled?: boolean
    disabledMessage?: string
    /** Resolves a post's current body, so a pending edit can show what it replaces. */
    resolveBody?: AgentAskResolveBody
    /** What this message is about: the screen chip and the attached item, shown inside the field above the text. */
    contextItems?: ContextItem[]
    /** Draws the drifting blue halo round the field. */
    halo?: boolean
    /** Rotating example prompts shown while the field is empty. */
    placeholders?: string[]
  }>(),
  {
    openAction: undefined,
    openQuestion: undefined,
    resolveBody: undefined,
    questionTyping: false,
    connect: undefined,
    pending: false,
    status: 'ready',
    retryable: false,
    disabled: false,
    disabledMessage: undefined,
    contextItems: () => [],
    halo: false,
    placeholders: undefined,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
  confirm: [id: string]
  decline: [id: string]
  answer: [value: string]
  'type-answer': []
  'answer-submit': [text: string]
  'skip-connect': []
  submit: [text: string, attachments: PendingImageHandoff[]]
  stop: []
  retry: []
  'remove-context': [id: string]
}>()

const { chat, composerRef } = useChatThreadSwitcher()

const { t } = useI18n()
const connections = useConnectionsStore()

const {
  images: attachedImages,
  onAttach,
  remove: removeAttachedImage,
  reset: resetAttachedImages,
  isUploadingAny,
  take: takeAttachedImages,
} = usePromptImageAttachments({ max: MAX_MESSAGE_ATTACHMENTS })

function onSubmit(text: string): void {
  emit('submit', text, takeAttachedImages())
}

watch(
  () => chat.activeThreadId,
  () => resetAttachedImages(),
)

const translate = (key: string, named?: Record<string, unknown>) => (named ? t(key, named) : t(key))

const actionAsk = computed<AgentAskModel | undefined>(() =>
  props.openAction ? askFromAction(props.openAction, translate, props.resolveBody) : undefined,
)

const questionAsk = computed<AgentAskModel | undefined>(() =>
  props.openQuestion ? askFromQuestion(props.openQuestion, translate) : undefined,
)

const connectAsk = computed<AgentAskModel | undefined>(() => {
  const connect = props.connect
  if (!connect) return undefined
  const state = connect.platform === 'linkedin' ? connections.linkedin : connections.whatsapp
  return askFromConnect(
    connect,
    {
      connecting: state.actionStatus === 'connecting',
      error: (state.actionStatus === 'error' ? state.error : undefined) ?? undefined,
    },
    translate,
  )
})

function onActionChoose(id: string): void {
  const action = props.openAction
  if (!action) return
  if (id === ACTION_CHOICE.confirm) emit('confirm', action.id)
  else if (id === ACTION_CHOICE.decline) emit('decline', action.id)
}

function onQuestionChoose(id: string): void {
  const question = props.openQuestion
  if (!question) return
  if (id === QUESTION_FREE_TEXT_CHOICE) {
    emit('type-answer')
    return
  }
  const index = questionOptionIndex(id)
  const option = index === null ? undefined : question.options[index]
  if (option !== undefined) emit('answer', option)
}

function onConnectChoose(id: string): void {
  const connect = props.connect
  if (!connect) return
  if (id === CONNECT_CHOICE.connect) void connections.connect(connect.platform)
  else if (id === CONNECT_CHOICE.skip) emit('skip-connect')
}
</script>

<template>
  <AgentAsk v-if="actionAsk" :ask="actionAsk" :pending="props.pending" @choose="onActionChoose" />
  <AgentAsk
    v-else-if="questionAsk && !props.questionTyping"
    :ask="questionAsk"
    :pending="props.pending"
    @choose="onQuestionChoose"
  />
  <div v-else-if="props.openQuestion" class="flex flex-col gap-2">
    <p class="text-caption text-text-tertiary">{{ props.openQuestion.question }}</p>
    <AppPromptInput
      ref="composerRef"
      :attach="false"
      :model-value="props.modelValue"
      :status="props.status"
      :retryable="props.retryable"
      @update:model-value="emit('update:modelValue', $event)"
      @submit="emit('answer-submit', $event)"
      @stop="emit('stop')"
      @retry="emit('retry')"
    />
  </div>
  <div v-else class="flex flex-col gap-8">
    <AgentAsk v-if="connectAsk" :ask="connectAsk" @choose="onConnectChoose" />
    <AppPromptInput
      ref="composerRef"
      :attach="!connectAsk"
      key-hint
      :halo="props.halo"
      :placeholders="props.placeholders"
      :has-context="props.contextItems.length > 0"
      :send-disabled="isUploadingAny"
      :has-attachments="attachedImages.length > 0"
      :model-value="props.modelValue"
      :status="props.status"
      :retryable="props.retryable"
      :disabled="props.disabled"
      :disabled-message="props.disabledMessage"
      @update:model-value="emit('update:modelValue', $event)"
      @submit="onSubmit"
      @attach="onAttach"
      @stop="emit('stop')"
      @retry="emit('retry')"
    >
      <template #attachments>
        <AppPromptAttachments :images="attachedImages" @remove="removeAttachedImage" />
      </template>
      <template #context>
        <AppContextChips :items="props.contextItems" @remove="emit('remove-context', $event)" />
      </template>
    </AppPromptInput>
  </div>
</template>
